const MODEL = 'GEMINI_2_5_FLASH';
const MAX_TOKENS = 400;

/**
 * Derives the correct API host, matching the pattern used by other editor AI
 * features in editor-plugin-ai/src/ui/prebuilt/utils.ts.
 *
 * On localhost (non-port-8081) and Atlaskit storybook hosts, routes to
 * pug.jira-dev.com so API calls work without a local backend.
 * In all other environments (staging, prod) uses window.location.origin.
 */
function deriveHost(): string {
	const {
		origin = '',
		port = '',
		hostname = '',
	} = typeof window !== 'undefined' && window.location ? window.location : {};

	if (
		(hostname === 'localhost' && port !== '8081') ||
		[
			'atlaskit-website-staging.stg-east.frontend.public.atl-paas.net',
			'statlas.prod.atl-paas.net',
			'atlaskit.atlassian.com',
		].some((h) => hostname === h)
	) {
		return 'https://pug.jira-dev.com';
	}

	return origin;
}

function getApiEndpoint(): string {
	return `${deriveHost()}/gateway/api/assist/api/ai/v2/ai-feature/heading-autocomplete`;
}

/**
 * Calls the heading autocomplete AI endpoint and returns the generated paragraph text.
 *
 * Uses the same host derivation as other editor AI features (deriveHost()), which
 * correctly routes to pug.jira-dev.com on localhost and to the gateway on production.
 *
 * @param pageContentMarkdown - The full page content serialised to Markdown, with a
 *   `<!-- GENERATE_HERE -->` comment injected after the heading to complete.
 * @param signal - AbortSignal so we can cancel in-flight requests when the user
 *   continues typing or navigates away.
 */
export async function callHeadingAutocompleteApi(
	pageContentMarkdown: string,
	signal: AbortSignal,
): Promise<string> {
	const response = await fetch(getApiEndpoint(), {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'X-Experience-Id': 'editor',
			'X-Product': 'confluence',
		},
		credentials: 'include',
		signal,
		body: JSON.stringify({
			ai_feature_input: {
				page_content_markdown: pageContentMarkdown,
				model: MODEL,
				max_tokens: MAX_TOKENS,
			},
		}),
	});

	if (!response.ok) {
		throw new Error(`Heading autocomplete API error: ${response.status} ${response.statusText}`);
	}

	const data = await response.json();
	const generatedParagraph: string | undefined = data?.ai_feature_output?.generated_paragraph;

	if (!generatedParagraph) {
		throw new Error('Heading autocomplete API returned an empty or malformed response');
	}

	return generatedParagraph;
}
